import type { AsyncResult } from '@shared/types/utilities';
import type { TileListPageFetcher } from '@tilelist/application/ports/TileListPageFetcher.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import type { PageLoadContext, PageLoadStrategy } from '@tilelist/domain/composition/strategies/PageLoadStrategy.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import { VARIATION_PRELOAD_COUNT, mergePreloadedVariations } from '@tilelist/domain/composition/VariationMerge.ts';

export type TileListPageLoadMode = 'initial' | 'initial-lite' | 'reload';

export type TileListPageLoaderDependencies = {
    pageFetcher: TileListPageFetcher;
    variationLoadingService: VariationLoader;
    strategy: PageLoadStrategy;
};

export type TileListPageLoadRequest = {
    mode: TileListPageLoadMode;
    ctx: PageLoadContext;
    entryRule?: string;
};

export type TileListPageLoader = (request: TileListPageLoadRequest) => AsyncResult<TileListPageLayout>;

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function createTileListPageLoader({
    pageFetcher,
    variationLoadingService,
    strategy,
}: TileListPageLoaderDependencies): TileListPageLoader {
    return async ({ mode, ctx, entryRule }) => {
        const tracking =
            ctx.requestContext.source === 'client-reload'
                ? { 'X-Tracking-Sfid': ctx.requestContext.tracking.sfid }
                : {};

        const result = await pageFetcher.getProducts(
            {
                rule: ctx.queryParams.rule,
                intents: [...strategy.intents(ctx)],
                'ranked.offset': ctx.queryParams.o ? Number(ctx.queryParams.o) : undefined,
                'ranked.sortOrder': ctx.queryParams.sortiertnach || undefined,
            },
            {
                ...tracking,
                ...(entryRule ? { 'x-entry-selection-rule': entryRule } : {}),
            },
        );

        if (result.state !== 'success') return result;

        const view = await strategy.compose(result.payload, ctx);

        const preloadedProducts = await variationLoadingService.loadBestVariationsForFirstNProducts(
            view.tileListItems,
            VARIATION_PRELOAD_COUNT[mode],
        );

        return {
            state: 'success',
            payload: {
                ...view,
                tileListItems: mergePreloadedVariations(view.tileListItems, preloadedProducts),
            },
        };
    };
}
